import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getTsiTicketsByClassificationAndMonth } from '../../services/apiService';

/**
 * TSI Classification Pivot Table component
 * Displays a pivot table of tickets by classification and month with clickable links
 * @param {Object} props - Component props
 * @param {Array} props.data - Classification data from the chart
 * @returns {JSX.Element} TSI Classification Pivot Table component
 */
const TsiClassificationPivotTable = ({ data }) => {
  const [pivotData, setPivotData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ticketsData, setTicketsData] = useState([]);
  const [selectedClassification, setSelectedClassification] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  // Extract unique months and classifications from the data
  const months = data ? [...new Set(data.map(item => item.month))] : [];
  
  const classifications = data ? [...new Set(data.flatMap(item => 
    Object.keys(item).filter(key => key !== 'month' && key !== 'total')
  ))] : [];

  // Process data into a pivot format
  useEffect(() => {
    if (!data || data.length === 0) return;

    const pivotObj = {};
    
    // Initialize the pivot object with all classifications and months
    classifications.forEach(classification => {
      pivotObj[classification] = {};
      months.forEach(month => {
        pivotObj[classification][month] = 0;
      });
    });
    
    // Fill in the data
    data.forEach(item => {
      const month = item.month;
      classifications.forEach(classification => {
        if (item[classification]) {
          pivotObj[classification][month] = item[classification];
        }
      });
    });
    
    setPivotData(pivotObj);
  }, [data]);

  // Calculate totals for each classification and month
  const calculateTotals = () => {
    if (!pivotData || Object.keys(pivotData).length === 0) return { rowTotals: {}, columnTotals: {} };
    
    const rowTotals = {};
    const columnTotals = {};
    
    // Initialize column totals
    months.forEach(month => {
      columnTotals[month] = 0;
    });
    
    // Calculate row and column totals
    classifications.forEach(classification => {
      rowTotals[classification] = 0;
      months.forEach(month => {
        const value = pivotData[classification][month] || 0;
        rowTotals[classification] += value;
        columnTotals[month] += value;
      });
    });
    
    return { rowTotals, columnTotals };
  };

  const { rowTotals, columnTotals } = calculateTotals();

  // Handle cell click to view tickets
  const handleCellClick = async (classification, month) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedCell({ classification, month });
      setSelectedClassification(classification);
      setSelectedMonth(month);
      
      // Extract year and month from the month string (e.g., "Aug 2024")
      const [monthName, year] = month.split(' ');
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
      const formattedMonth = `${year}-${monthIndex.toString().padStart(2, '0')}`;
      
      // Call API to get tickets for this classification and month
      const response = await getTsiTicketsByClassificationAndMonth(classification, formattedMonth);
      
      if (response.success) {
        // Set tickets data and open dialog
        setTicketsData(response.data);
        setDialogOpen(true);
      } else {
        throw new Error(response.message || 'Failed to fetch tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
      setSelectedCell(null);
    }
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTicketsData([]);
  };

  // Get color based on value (higher values = darker green)
  const getCellColor = (value) => {
    if (!value || value === 0) return '#FFFFFF';
    
    // Define color ranges
    if (value < 5) return '#E8F5E9';
    if (value < 10) return '#C8E6C9';
    if (value < 20) return '#A5D6A7';
    if (value < 30) return '#81C784';
    if (value < 50) return '#66BB6A';
    if (value < 75) return '#4CAF50';
    if (value < 100) return '#43A047';
    return '#2E7D32';
  };

  // Get text color based on background color (for contrast)
  const getTextColor = (bgColor) => {
    // For darker greens, use white text
    if (bgColor === '#43A047' || bgColor === '#2E7D32') return '#FFFFFF';
    return '#000000';
  };

  if (!data || data.length === 0) {
    return (
      <Box mt={2}>
        <Typography variant="subtitle1" color="textSecondary">
          No data available for pivot table
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Box 
        sx={{ 
          backgroundColor: '#1976d2', 
          color: 'white', 
          p: 1.5, 
          borderTopLeftRadius: 4, 
          borderTopRightRadius: 4,
          mb: 0
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Classification Pivot Table (Click on a cell to view tickets)
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <TableContainer component={Paper} sx={{ maxHeight: 600, width: '100%', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Classification</TableCell>
              {months.map(month => (
                <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {month}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {classifications.map(classification => (
              <TableRow key={classification} hover>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  {classification}
                </TableCell>
                {months.map(month => {
                  const value = pivotData[classification]?.[month] || 0;
                  const bgColor = getCellColor(value);
                  const textColor = getTextColor(bgColor);
                  
                  return (
                    <TableCell 
                      key={`${classification}-${month}`} 
                      align="center"
                      sx={{ 
                        backgroundColor: bgColor,
                        color: textColor,
                        cursor: value > 0 ? 'pointer' : 'default',
                        '&:hover': value > 0 ? { 
                          backgroundColor: '#81C784',
                          color: '#000000'
                        } : {}
                      }}
                      onClick={() => value > 0 && handleCellClick(classification, month)}
                    >
                      {loading && selectedCell?.classification === classification && selectedCell?.month === month ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Tooltip title={value > 0 ? `Click to view ${value} tickets` : "No tickets"} arrow>
                          <Box component="span">
                            {value > 0 ? (
                              <Link 
                                component="button"
                                underline="none"
                                color="inherit"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent default link behavior
                                }}
                              >
                                {value}
                              </Link>
                            ) : (
                              value
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#e0e0e0'
                  }}
                >
                  {rowTotals[classification] || 0}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
              {months.map(month => (
                <TableCell 
                  key={`total-${month}`} 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#e0e0e0'
                  }}
                >
                  {columnTotals[month] || 0}
                </TableCell>
              ))}
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: '#bdbdbd'
                }}
              >
                {Object.values(columnTotals).reduce((sum, val) => sum + val, 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog to display tickets */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedClassification} Tickets for {selectedMonth}
            </Typography>
            <IconButton onClick={handleDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Total tickets: {ticketsData.length}
          </Typography>
          
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Component</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Closed Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticketsData.map(ticket => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Link 
                        href={ticket.url || 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/' + ticket.id}   
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {ticket.id}
                      </Link>
                    </TableCell>
                    <TableCell>{ticket.title || ''}</TableCell>
                    <TableCell>{ticket.priority || ''}</TableCell>
                    <TableCell>{ticket.componentPath || ''}</TableCell>
                    <TableCell>{ticket.environment || ''}</TableCell>
                    <TableCell>{ticket.state || ''}</TableCell>
                    <TableCell>
                      {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell>
                      {ticket.closedDate ? new Date(ticket.closedDate).toLocaleDateString() : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TsiClassificationPivotTable;
